import {useCallback, useEffect, useRef, useState} from 'react'
import type {ImportSourceType} from '../core/_requests'

export type ImportDataDropdownAppearance = 'labeled' | 'icon' | 'composer'

export const IMPORT_MENU_ITEMS: {
  key: string
  label: string
  icon: string
  action: 'uploadFile' | 'uploadFolder' | 'importExternal'
  sourceType?: ImportSourceType
}[] = [
  {key: 'upload-file', label: 'Upload Files', icon: 'fas fa-file-arrow-up', action: 'uploadFile'},
  {key: 'upload-folder', label: 'Upload Folder', icon: 'fas fa-folder-open', action: 'uploadFolder'},
  {key: 'import-s3', label: 'Amazon S3', icon: 'fab fa-aws', action: 'importExternal', sourceType: 's3'},
  {key: 'import-https', label: 'HTTP / HTTPS', icon: 'fas fa-link', action: 'importExternal', sourceType: 'https'},
  {key: 'import-ftp', label: 'FTP', icon: 'fas fa-server', action: 'importExternal', sourceType: 'ftp'},
]

export interface ImportDataDropdownProps {
  appearance: ImportDataDropdownAppearance
  onUploadFile: () => void
  onUploadFolder: () => void
  onImportS3: (sourceType?: ImportSourceType) => void
  testId?: string
  /** Why Upload Files and Upload Folder are off here, if they are. */
  uploadDisabledReason?: string
  /**
   * Why the server-side imports (S3, HTTPS, FTP) are off here, if they are: they always land in My
   * Files, so not from a shared folder.
   */
  importDisabledReason?: string
}

/**
 * Shared Import Data menu (upload files/folder, S3/HTTPS/FTP). Used by the Files panel header
 * and the AI chat composer so both fire the same FileTable handlers.
 */
export const ImportDataDropdown = ({
  appearance,
  onUploadFile,
  onUploadFolder,
  onImportS3,
  testId = 'omics-files-import-btn',
  uploadDisabledReason,
  importDisabledReason,
}: ImportDataDropdownProps): JSX.Element => {
  const items = IMPORT_MENU_ITEMS
  const triggerLabel = 'Import Data'
  const disabledReason = (item: (typeof IMPORT_MENU_ITEMS)[number]) =>
    item.action === 'importExternal' ? importDisabledReason : uploadDisabledReason
  // Shown, light grey, rather than taken away when nothing in it can be done here.
  const triggerDisabledReason =
    uploadDisabledReason && importDisabledReason ? uploadDisabledReason : undefined
  const [open, setOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const close = useCallback(() => setOpen(false), [])

  useEffect(() => {
    if (!open) return
    const onClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        close()
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [open, close])

  const handleItemClick = (item: (typeof IMPORT_MENU_ITEMS)[number]) => {
    if (disabledReason(item)) {
      return
    }
    close()
    switch (item.action) {
      case 'uploadFile':
        onUploadFile()
        break
      case 'uploadFolder':
        onUploadFolder()
        break
      case 'importExternal':
        onImportS3(item.sourceType)
        break
    }
  }

  const menuClass =
    appearance === 'composer'
      ? 'aui-composer-import__menu'
      : appearance === 'icon'
        ? 'ob-files-panel__dropdown-menu'
        : undefined

  const menu = open && (
    <div
      className={menuClass}
      style={
        appearance === 'labeled'
          ? {
              position: 'absolute',
              top: '100%',
              left: 0,
              zIndex: 1050,
              minWidth: '200px',
              maxWidth: 'calc(100vw - 2rem)',
              background: 'var(--kt-card-bg, #fff)',
              border: '1px solid var(--kt-border-color, #e4e6ef)',
              borderRadius: '0.475rem',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
              padding: '0.35rem 0',
              marginTop: '4px',
            }
          : undefined
      }
    >
      {items.map((item, idx) => (
        <div key={item.key}>
          {idx === 2 && (
            <hr
              style={{
                margin: '0.35rem 0.75rem',
                borderColor: 'var(--kt-border-color, #e4e6ef)',
              }}
            />
          )}
          <button
            type='button'
            className={`btn btn-sm w-100${disabledReason(item) ? ' ob-toolbar-disabled' : ''}`}
            aria-disabled={disabledReason(item) ? true : undefined}
            title={disabledReason(item)}
            data-testid={`omics-files-import-${item.key}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.4rem 0.75rem',
              fontSize: 'var(--ob-font-size-sm, 13px)',
              fontWeight: '400',
              borderRadius: 0,
              textAlign: 'left',
              transition: 'background 0.15s ease',
            }}
            onMouseEnter={(e) => {
              if (disabledReason(item)) return
              e.currentTarget.style.background = 'var(--kt-gray-100, #f5f8fa)'
              e.currentTarget.style.color = '#888e00'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = ''
              e.currentTarget.style.color = ''
            }}
            onClick={() => handleItemClick(item)}
          >
            <i className={item.icon} style={{width: '18px', textAlign: 'center'}} />
            <span>{item.label}</span>
          </button>
        </div>
      ))}
    </div>
  )

  const trigger = (
    <button
      type='button'
      className={[
        appearance === 'composer'
          ? `aui-composer-dictate${open ? ' active' : ''}`
          : appearance === 'icon'
            ? `ob-files-panel__icon-btn${open ? ' active' : ''}`
            : 'btn btn-sm',
        triggerDisabledReason ? 'ob-toolbar-disabled' : '',
      ]
        .filter(Boolean)
        .join(' ')}
      aria-disabled={triggerDisabledReason ? true : undefined}
      style={
        appearance === 'labeled'
          ? {
              padding: 'var(--ob-spacing-1) var(--ob-spacing-2)',
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--ob-spacing-1)',
              fontSize: 'var(--ob-font-size-sm)',
              fontWeight: '500',
              transition: 'all 0.2s ease',
            }
          : undefined
      }
      onMouseEnter={
        appearance === 'labeled' && !triggerDisabledReason
          ? (e) => {
              e.currentTarget.style.color = '#dbde8e'
              e.currentTarget.style.borderColor = '#dbde8e'
            }
          : undefined
      }
      onMouseLeave={
        appearance === 'labeled'
          ? (e) => {
              e.currentTarget.style.color = ''
              e.currentTarget.style.borderColor = ''
            }
          : undefined
      }
      onClick={() => {
        if (!triggerDisabledReason) setOpen((prev) => !prev)
      }}
      title={triggerDisabledReason ?? triggerLabel}
      aria-label={triggerLabel}
      aria-expanded={open}
      data-testid={testId}
    >
      <i className='fas fa-cloud-arrow-up' />
      {appearance === 'labeled' && (
        <>
          <span className='ob-file-header__btn-label'>{triggerLabel}</span>
          <i className='fas fa-caret-down' style={{fontSize: 'var(--ob-font-size-xs)', marginLeft: '2px'}} />
        </>
      )}
    </button>
  )

  if (appearance === 'labeled') {
    return (
      <li style={{margin: 0, position: 'relative'}}>
        <div ref={dropdownRef}>
          {trigger}
          {menu}
        </div>
      </li>
    )
  }

  return (
    <div
      className={appearance === 'composer' ? 'aui-composer-import' : 'ob-files-panel__dropdown'}
      ref={dropdownRef}
    >
      {trigger}
      {menu}
    </div>
  )
}
